import { supabase } from "./supabaseClient";
import { LocalizedText, QuestionConfig } from "../types";

// Optional segment filter applied across the whole dashboard.
export interface SegmentFilter { question_id: string; value: string; }

export interface DashboardSummary {
  total: number;
  completed: number;
  imported: number;
  completion_rate: number;
  avg_duration_seconds: number | null;
  by_day: { day: string; count: number }[];
}

export interface ValueCount { value: string | number; count: number }

export interface QuestionStat {
  question_id: string;
  order_index: number;
  question_text: LocalizedText;
  question_type: string;
  config: QuestionConfig;
  n: number;
  // choice / checkbox
  counts?: ValueCount[];
  multi?: boolean;
  // rating
  mean?: number; median?: number; mode?: number; stddev?: number; min?: number; max?: number;
  histogram?: ValueCount[];
  // text
  samples?: string[];
  top_words?: ValueCount[];
}

export interface CrosstabCell { r: string; c: string; n: number }

// ── Label helpers ────────────────────────────────────────────────────────────
export const getText = (t: any): string => (t && (t.en || Object.values(t)[0])) || '';

// Map a stored answer value (option id / number / string) to a human label using
// the question's config.options. Falls back to the raw value.
export function valueLabel(config: QuestionConfig | undefined, value: string | number): string {
  const opt = config?.options?.find((o) => String(o.id) === String(value));
  if (opt) return getText(opt.label);
  return String(value);
}

// ── RPC wrappers ─────────────────────────────────────────────────────────────
export async function getDashboardSummary(surveyId: string): Promise<DashboardSummary> {
  const { data, error } = await supabase.rpc('survey_dashboard_summary', { p_survey_id: surveyId });
  if (error) throw error;
  return data as DashboardSummary;
}

export async function getQuestionStats(surveyId: string, filter?: SegmentFilter | null): Promise<QuestionStat[]> {
  const { data, error } = await supabase.rpc('survey_question_stats', {
    p_survey_id: surveyId, p_filter: filter ?? null,
  });
  if (error) throw error;
  return (data || []) as QuestionStat[];
}

export async function getCrosstab(surveyId: string, q1: string, q2: string, filter?: SegmentFilter | null): Promise<CrosstabCell[]> {
  const { data, error } = await supabase.rpc('survey_crosstab', {
    p_survey_id: surveyId, p_q1: q1, p_q2: q2, p_filter: filter ?? null,
  });
  if (error) throw error;
  return (data?.cells || []) as CrosstabCell[];
}

export interface NumericPairs {
  n: number; sumX: number; sumY: number; sumXY: number; sumX2: number; sumY2: number;
  grid: { x: number; y: number; n: number }[];
}
export async function getNumericPairs(surveyId: string, q1: string, q2: string, filter?: SegmentFilter | null): Promise<NumericPairs> {
  const { data, error } = await supabase.rpc('survey_numeric_pairs', {
    p_survey_id: surveyId, p_q1: q1, p_q2: q2, p_filter: filter ?? null,
  });
  if (error) throw error;
  return data as NumericPairs;
}

// Pivot sparse crosstab cells into { rows, cols, matrix } for rendering + tests.
export function pivotCrosstab(cells: CrosstabCell[]): { rows: string[]; cols: string[]; matrix: number[][] } {
  const rows = Array.from(new Set(cells.map((c) => c.r)));
  const cols = Array.from(new Set(cells.map((c) => c.c)));
  const ri = new Map(rows.map((r, i) => [r, i]));
  const ci = new Map(cols.map((c, i) => [c, i]));
  const matrix = rows.map(() => cols.map(() => 0));
  for (const cell of cells) matrix[ri.get(cell.r)!][ci.get(cell.c)!] = cell.n;
  return { rows, cols, matrix };
}
